import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface Project {
  _id: string;
  title: string;
  slug: string;
  description: string;
  imageUrl: string;
  category: string;
  network: string;
  tags: string[];
  featured: boolean;
}

interface LabsProject {
  _id: string;
  title: string;
  description: string;
  content: string;
  keyFeatures?: string;
  whatMakesDifferent?: string;
  useCases?: string;
  status: 'beta' | 'in-progress' | 'soon' | 'completed';
  imageUrl?: string;
  primaryButton?: {
    enabled: boolean;
    text: string;
    url: string;
  };
  secondaryButton?: {
    enabled: boolean;
    text: string;
    url: string;
  };
  views: number;
  likes: number;
  createdAt: string;
  updatedAt: string;
}

const getStatusDisplay = (status: 'beta' | 'in-progress' | 'soon' | 'completed') => {
  switch (status) {
    case 'beta':
      return { text: 'Beta', color: 'bg-indigo-500' };
    case 'in-progress':
      return { text: 'In Progress', color: 'bg-blue-500' };
    case 'soon':
      return { text: 'Soon', color: 'bg-slate-500' };
    case 'completed':
      return { text: 'Completed', color: 'bg-indigo-600' };
    default:
      return { text: 'Completed', color: 'bg-indigo-600' };
  }
};

const LabsPage: React.FC = () => {
  const [labsProjects, setLabsProjects] = useState<LabsProject[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{url: string, title: string} | null>(null);

  useEffect(() => {
    const fetchLabsProjects = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const response = await fetch(`${apiUrl}/api/labs`);
        
        if (response.ok) {
          const result = await response.json();
          
          // Check if the API response has the expected structure
          if (result.success && Array.isArray(result.data)) {
            // РџРѕРєР°Р·СѓС”РјРѕ РІСЃС– РїСЂРѕРµРєС‚Рё РЅРµР·Р°Р»РµР¶РЅРѕ РІС–Рґ СЃС‚Р°С‚СѓСЃСѓ
            const publicProjects = result.data;
            setLabsProjects(publicProjects);
          } else {
            console.error('Unexpected API response structure:', result);
            setError('Invalid response from server');
          }
        } else {
          setError('Failed to load projects');
        }
      } catch (err) {
        console.error('Error fetching labs projects:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    // Fetch projects for Services modal
    const fetchProjects = async () => {
      try {
        const response = await axios.get('/api/projects?status=active&isPublished=true');
        if (response.data.success) {
          // Filter to show only Services projects in Services modal
          const filteredProjects = response.data.data.projects.filter(
            (project: Project) => project.category?.toLowerCase() === 'services'
          );
          setProjects(filteredProjects);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchLabsProjects();
    fetchProjects();
  }, []);

  // Handle Escape key to close image modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  if (loading) {
    return (
      <Layout 
        showServicesModal={showServicesModal}
        setShowServicesModal={setShowServicesModal}
        currentPage="labs"
        projects={projects}
      >
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-xl">Loading Labs projects...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout 
        showServicesModal={showServicesModal}
        setShowServicesModal={setShowServicesModal}
        currentPage="labs"
        projects={projects}
      >
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl text-red-400 mb-4">вљ пёЏ {error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      showServicesModal={showServicesModal}
      setShowServicesModal={setShowServicesModal}
      currentPage="labs"
      projects={projects}
    >
      <div className="min-h-screen bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6 text-white">
              DUSKPROOF <span className="pastel-labs">Labs</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Web3 Infrastructure, Enterprise Standards
            </p>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed mt-2">
              Building decentralized solutions with Web2 reliability and scalability
            </p>
          </div>

          {/* Projects Section */}
          {labsProjects.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-8xl mb-8">рџ”¬</div>
              <h2 className="text-3xl font-bold mb-6">Coming Soon</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
                We're cooking up something amazing! Our Labs section will showcase experimental projects, 
                research tools, and cutting-edge blockchain innovations.
              </p>
              
              {/* Features Preview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-4xl mx-auto">
                <div className="dashboard-block p-6 text-center">
                  <div className="text-4xl mb-4">вљЎ</div>
                  <h3 className="text-xl font-semibold mb-3">Experimental Tools</h3>
                  <p className="text-gray-400">
                    Cutting-edge blockchain utilities and research tools
                  </p>
                </div>
                
                <div className="dashboard-block p-6 text-center">
                  <div className="text-4xl mb-4">рџ§Є</div>
                  <h3 className="text-xl font-semibold mb-3">Beta Projects</h3>
                  <p className="text-gray-400">
                    Early access to our latest innovations and experiments
                  </p>
                </div>
                
                <div className="dashboard-block p-6 text-center">
                  <div className="text-4xl mb-4">рџљЂ</div>
                  <h3 className="text-xl font-semibold mb-3">Open Source</h3>
                  <p className="text-gray-400">
                    Community-driven development and transparent research
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {labsProjects.map((project) => {
                const statusInfo = getStatusDisplay(project.status);
                
                return (
                  <div key={project._id} className="dashboard-block p-8">
                    <div className="flex flex-col lg:flex-row gap-8 lg:items-center">
                      {/* Project Image */}
                      {project.imageUrl && (
                        <div className="lg:w-1/2 xl:w-2/5 flex flex-col justify-center">
                          <div 
                            className="relative overflow-hidden rounded-xl shadow-2xl group cursor-pointer"
                            onClick={() => setSelectedImage({url: project.imageUrl!, title: project.title})}
                          >
                            <img 
                              src={getFullImageUrl(project.imageUrl)} 
                              alt={project.title}
                              className="w-full h-[400px] object-cover rounded-xl transition-transform duration-300 group-hover:scale-110"
                              onError={handleImageError}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-xl pointer-events-none"></div>
                            
                            {/* Magnifier Icon on Hover */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 rounded-xl">
                              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          {(project.primaryButton?.enabled || project.secondaryButton?.enabled) && (
                            <div className="flex flex-col gap-3 mt-6">
                              {project.primaryButton?.enabled && project.primaryButton.url && (
                                <a
                                  href={project.primaryButton.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm text-center"
                                  style={{ 
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)', 
                                    border: '1px solid rgba(99, 102, 241, 0.3)',
                                    color: '#a5b4fc'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.2)';
                                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                                    e.currentTarget.style.color = '#c7d2fe';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                                    e.currentTarget.style.color = '#a5b4fc';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  <span>{project.primaryButton.text || 'VIEW PROJECT'}</span>
                                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                              
                              {project.secondaryButton?.enabled && project.secondaryButton.url && (
                                <a
                                  href={project.secondaryButton.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm text-center"
                                  style={{ 
                                    backgroundColor: 'rgba(75, 85, 99, 0.1)', 
                                    border: '1px solid rgba(75, 85, 99, 0.3)',
                                    color: '#9ca3af'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(75, 85, 99, 0.2)';
                                    e.currentTarget.style.borderColor = 'rgba(75, 85, 99, 0.5)';
                                    e.currentTarget.style.color = '#d1d5db';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(75, 85, 99, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(75, 85, 99, 0.3)';
                                    e.currentTarget.style.color = '#9ca3af';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  <span>{project.secondaryButton.text || 'READ MORE'}</span>
                                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Project Content */}
                      <div className={project.imageUrl ? "lg:w-1/2 xl:w-3/5 lg:pl-8" : "w-full"}>
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                          <h2 className="text-3xl font-bold mb-4 sm:mb-0">{project.title}</h2>
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white ${statusInfo.color}`}>
                            {statusInfo.text}
                          </div>
                        </div>
                        
                        {/* Description */}
                        <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                          {project.description}
                        </p>
                        
                        {/* Action Buttons for projects without image */}
                        {!project.imageUrl && (project.primaryButton?.enabled || project.secondaryButton?.enabled) && (
                          <div className="flex flex-wrap gap-4 mb-8">
                            {project.primaryButton?.enabled && project.primaryButton.url && (
                              <a
                                href={project.primaryButton.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                                style={{ 
                                  backgroundColor: 'rgba(99, 102, 241, 0.1)', 
                                  border: '1px solid rgba(99, 102, 241, 0.3)',
                                  color: '#a5b4fc'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.2)';
                                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                                  e.currentTarget.style.color = '#c7d2fe';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                                  e.currentTarget.style.color = '#a5b4fc';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <span>{project.primaryButton.text || 'VIEW PROJECT'}</span>
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            )}
                            
                            {project.secondaryButton?.enabled && project.secondaryButton.url && (
                              <a
                                href={project.secondaryButton.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                                style={{ 
                                  backgroundColor: 'rgba(75, 85, 99, 0.1)', 
                                  border: '1px solid rgba(75, 85, 99, 0.3)',
                                  color: '#9ca3af'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(75, 85, 99, 0.2)';
                                  e.currentTarget.style.borderColor = 'rgba(75, 85, 99, 0.5)';
                                  e.currentTarget.style.color = '#d1d5db';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(75, 85, 99, 0.1)';
                                  e.currentTarget.style.borderColor = 'rgba(75, 85, 99, 0.3)';
                                  e.currentTarget.style.color = '#9ca3af';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <span>{project.secondaryButton.text || 'READ MORE'}</span>
                                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </a>
                            )}
                          </div>
                        )}
                        
                        {/* Features Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Key Features */}
                          {project.keyFeatures && (
                            <div>
                              <h4 className="text-lg font-semibold mb-3 flex items-center">
                                <span className="mr-2">рџ“Љ</span>
                                Key Features
                              </h4>
                              <div className="text-sm text-gray-400 space-y-1">
                                {project.keyFeatures.split('\n').filter(line => line.trim()).map((feature, index) => (
                                  <div key={index} className="flex items-start">
                                    <span className="text-gray-400 mr-2">вЂў</span>
                                    <span>{feature.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* What Makes Different */}
                          {project.whatMakesDifferent && (
                            <div>
                              <h4 className="text-lg font-semibold mb-3 flex items-center">
                                <span className="mr-2">рџ“Њ</span>
                                What's Different
                              </h4>
                              <div className="text-sm text-gray-400 space-y-1">
                                {project.whatMakesDifferent.split('\n').filter(line => line.trim()).map((diff, index) => (
                                  <div key={index} className="flex items-start">
                                    <span className="text-gray-400 mr-2">вЂў</span>
                                    <span>{diff.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Use Cases */}
                          {project.useCases && (
                            <div>
                              <h4 className="text-lg font-semibold mb-3 flex items-center">
                                <span className="mr-2">рџЋЇ</span>
                                Use Cases
                              </h4>
                              <div className="text-sm text-gray-400 space-y-1">
                                {project.useCases.split('\n').filter(line => line.trim()).map((useCase, index) => (
                                  <div key={index} className="flex items-start">
                                    <span className="text-gray-400 mr-2">вЂў</span>
                                    <span>{useCase.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>


                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative max-w-7xl max-h-[90vh] mx-4">
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image */}
            <img
              src={getFullImageUrl(selectedImage.url)}
              alt={selectedImage.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onError={handleImageError}
            />
            
            {/* Title */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <h3 className="text-white text-xl font-semibold">{selectedImage.title}</h3>
            </div>
          </div>
          
          {/* Click outside to close */}
          <div 
            className="absolute inset-0 -z-10" 
            onClick={() => setSelectedImage(null)}
          ></div>
        </div>
      )}
    </Layout>
  );
};

export default LabsPage;


